// @ts-nocheck
import { WindowContext, WindowContextModel } from "@store/Framework/Components/WindowContext/WindowContext";
import debounce from "lodash/debounce";
import findLastIndex from "lodash/findLastIndex";
import React, { FC, PropsWithChildren, useEffect, useState } from "react";
import { useTheme } from "styled-components";

export interface WindowContextProviderProps {
    debounceDelay?: Parameters<typeof debounce>[1];
    debounceOptions?: Parameters<typeof debounce>[2];
}

const getClientWidth = (): WindowContextModel["clientWidth"] => {
    if (typeof document === "undefined") {
        return undefined;
    }

    return document.documentElement.clientWidth;
};

const getClientHeight = (): WindowContextModel["clientHeight"] => {
    if (typeof document === "undefined") {
        return undefined;
    }

    return document.documentElement.clientHeight;
};

const getCurrentBreakpoint = (theme: Toolkit.Theme): WindowContextModel["currentBreakpoint"] => {
    const clientWidth = getClientWidth();

    if (typeof document === "undefined" || !clientWidth) {
        return undefined;
    }

    const breakpointIndex = findLastIndex(theme.breakpoints.values, x => clientWidth >= x);

    return breakpointIndex !== -1 ? theme.breakpoints.keys[breakpointIndex] : undefined;
};

const getContext = (theme: Toolkit.Theme): WindowContextModel => ({
    clientWidth: getClientWidth(),
    clientHeight: getClientHeight(),
    currentBreakpoint: getCurrentBreakpoint(theme),
});

export const WindowContextProvider: FC<PropsWithChildren<WindowContextProviderProps>> = ({
    children,
    debounceDelay = 150,
    debounceOptions,
}) => {
    const theme = useTheme();
    const [context, setContext] = useState<WindowContextModel>(getContext(theme));

    useEffect(() => {
        const onResize = debounce(() => setContext(getContext(theme)), debounceDelay, debounceOptions);

        window.addEventListener("resize", onResize, { passive: true });

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    return <WindowContext.Provider value={context}>{children}</WindowContext.Provider>;
};
